/**
 * Step Navigator component - step-by-step navigation
 */

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "#app/components/ui/button";
import { cn } from "#app/utils/misc";

export interface Step {
  id: string;
  title: string;
  level: number;
}

export interface StepNavigatorProps {
  steps: Step[];
  currentStep: number;
  onStepChange: (step: number) => void;
  completedSteps?: string[];
  onNext?: () => void;
  onPrevious?: () => void;
  className?: string;
}

export function StepNavigator({
  steps,
  currentStep,
  onStepChange,
  completedSteps = [],
  onNext,
  onPrevious,
  className,
}: StepNavigatorProps) {
  const { t } = useTranslation("docs");
  const totalSteps = steps.length;
  const canGoPrevious = currentStep > 0;
  const canGoNext = currentStep < totalSteps - 1;

  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious();
    } else if (canGoPrevious) {
      onStepChange(currentStep - 1);
    }
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
    } else if (canGoNext) {
      onStepChange(currentStep + 1);
    }
  };

  if (totalSteps === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between px-4 py-2.5 border-t border-[#DDDDDD] bg-white dark:bg-zinc-900",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <Button
          variant="gradient-outline"
          size="sm"
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          {t("previous")}
        </Button>
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t("stepOf", { current: currentStep + 1, total: totalSteps })}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {/* Step indicators */}
        <div className="flex items-center gap-1">
          {steps.map((step, index) => {
            const isCompleted = completedSteps.includes(step.id);
            const isCurrent = index === currentStep;
            return (
              <button
                key={step.id}
                onClick={() => onStepChange(index)}
                className={cn(
                  "w-2 h-2 rounded-full transition-colors",
                  isCurrent && "bg-[#535EA4] w-3 h-3",
                  isCompleted && !isCurrent && "bg-green-500",
                  !isCompleted && !isCurrent && "bg-gray-300 hover:bg-gray-400",
                )}
                title={step.title}
              />
            );
          })}
        </div>
        <Button
          variant="gradient"
          size="sm"
          onClick={handleNext}
          disabled={!canGoNext}
          className="flex items-center gap-2"
        >
          {t("next")}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
